import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Popover } from '@c2fo/liquidity';
import invoices from '@/enterprise/assets/invoicesgrey.svg?url';
import EmptyRender from '@/enterprise/components/EmptyRender';
import NoDataState from '@/enterprise/components/NoDataState';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableDisplay,
  TableSortArrow,
} from '@/enterprise/components/Table';
import useLockedInvoices from '@/enterprise/data/useLockedInvoices';
import useDataDog from '@/reporting/DataDog';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useUserHasIndiaDivisions from '@/utils/useUserHasIndiaDivisions';
import { IneligibleInvoiceSortKey, IneligibleInvoiceSource, IneligibleInvoiceUrlState } from './types';
import { getIneligibilityReasonConfig, mapUrlStateToApiSearchFilters } from './utils';

export const IneligibleInvoicesTable = ({
  currentCursor,
  setNextCursor,
  urlState,
  handleSort,
  source,
}: {
  currentCursor: string | null;
  setNextCursor: (cursor: string | null) => void;
  urlState: IneligibleInvoiceUrlState;
  handleSort: (sortKey: IneligibleInvoiceSortKey) => void;
  source: IneligibleInvoiceSource;
}) => {
  const { t } = useTranslation();
  const { asDateStringText, asCurrency } = useLocaleFormat();
  const { addAction } = useDataDog();
  const hasIndiaDivisions = useUserHasIndiaDivisions();
  const { makerOrganizationUuid, takerMarkets } = source;

  const search = useMemo(() => mapUrlStateToApiSearchFilters({ urlState }), [urlState]);
  const { sort } = urlState;

  const { data } = useLockedInvoices({
    makerOrganizationUuid,
    takerMarkets,
    options: {
      sortBy: sort.key,
      sortDirection: sort.direction === 'asc' ? 'ASC' : 'DESC',
      ...(currentCursor && { cursor: currentCursor }),
      limit: urlState.pageSize,
    },
    ...(!!search && {
      search,
    }),
  });

  const nextCursor = data?.pageInfo?.nextCursor ?? null;

  useEffect(() => {
    setNextCursor(nextCursor);
  }, [nextCursor, setNextCursor]);

  const ineligibleInvoices = useMemo(() => data?.invoices ?? [], [data?.invoices]);

  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead isSorted={sort.key === 'voucherId'} onClick={() => handleSort('voucherId')}>
              <TableSortArrow accessorKey="voucherId" sort={sort}>
                {t('core.invoiceId')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'originalAmount'} onClick={() => handleSort('originalAmount')}>
              <TableSortArrow accessorKey="originalAmount" sort={sort} textRight>
                {t('core.invoiceAmount')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'originalDepositDate'} onClick={() => handleSort('originalDepositDate')}>
              <TableSortArrow accessorKey="originalDepositDate" sort={sort}>
                {hasIndiaDivisions ? t('core.tentativePaymentDate') : t('core.originalDueDate')}
              </TableSortArrow>
            </TableHead>
            <TableHead className="text-right">{t('invoiceCentral.ineligibilityReason')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ineligibleInvoices.length > 0 ? (
            ineligibleInvoices.map((ineligibleInvoice) => {
              const ineligibilityReason = getIneligibilityReasonConfig(ineligibleInvoice);

              if (!ineligibilityReason) {
                addAction('invoice-central::ineligible-invoice-without-reason', {
                  invoiceId: ineligibleInvoice.voucherId,
                  supplierDivisionUuid: ineligibleInvoice.supplierDivisionUuid,
                  marketUuid: ineligibleInvoice.marketUuid,
                  amount: ineligibleInvoice.originalAmount,
                });
              }

              return (
                <TableRow key={ineligibleInvoice.voucherId}>
                  {/* Invoice Id */}
                  <TableCell>
                    <div className="max-w-80 whitespace-pre-wrap break-words">{ineligibleInvoice.voucherId}</div>
                  </TableCell>
                  {/* Invoice Amount */}
                  <TableCell className="text-right">
                    {asCurrency(ineligibleInvoice.originalAmount, ineligibleInvoice.currency)}
                  </TableCell>
                  {/* Estimated Deposit */}
                  <TableCell>
                    {ineligibleInvoice.originalDepositDate &&
                      asDateStringText({ date: ineligibleInvoice.originalDepositDate })}
                  </TableCell>
                  {/* Ineligibility Reason */}
                  <TableCell className="flex justify-end">
                    <EmptyRender condition={!!ineligibilityReason}>
                      {ineligibilityReason && (
                        <Popover>
                          <Popover.Trigger aria-label="Ineligibility reason information">
                            <Chip
                              label={t(ineligibilityReason.labelKey)}
                              type={ineligibilityReason.chipType}
                              useHoverStyles
                            />
                          </Popover.Trigger>
                          <Popover.Content arrow collisionPadding={24}>
                            <div className="w-72 space-y-2 text-center text-sm">
                              <div>{t(ineligibilityReason.tooltipKey)}</div>
                            </div>
                          </Popover.Content>
                        </Popover>
                      )}
                    </EmptyRender>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableDisplay colSpan={4}>
              <NoDataState
                className="py-8"
                icon={<img src={invoices} alt="emptystatements" className="h-28 w-28" />}
                title={t('invoices.noAvailableInvoices')}
                message={t('invoices.noAvailableEligibleInvoicesDescription')}
              />
            </TableDisplay>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
